import React from "react";
import { Grid, IconButton, Icon, FormHelperText } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import FormField from "components/FormField/FormField";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

interface MaterialsNoPlantFieldsProps {
  disabled?: boolean;
  action?: "create" | "update";
}

export default function MaterialsNoPlantFields({
  disabled = false,
  action = "create",
}: MaterialsNoPlantFieldsProps) {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "materialsNoPlant",
  });

  const addMaterial = () => {
    const isFirstMaterial = fields.length === 0;
    const newMaterial = {
      name: "",
      percentage: isFirstMaterial ? 100 : "",
      rate: "",
    };

    if (action === "update") {
      append({ ...newMaterial, id: null });
    } else {
      append(newMaterial);
    }
  };

  const removeMaterial = (index: number) => {
    remove(index);
  };

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6">Materials</MDTypography>
        {!disabled && (
          <MDButton size="small" color="blue" variant="contained" onClick={addMaterial}>
            <Icon>add</Icon>
            Add Material
          </MDButton>
        )}
      </MDBox>
      {errors.materialsNoPlant?.message && (
        <FormHelperText error sx={{ marginLeft: 0, marginBottom: 2 }}>
          {String(errors.materialsNoPlant.message)}
        </FormHelperText>
      )}

      <Grid container spacing={1}>
        {fields.map((field, index) => (
          <React.Fragment key={field.id}>
            {action === "update" && (
              <input type="hidden" {...register(`materialsNoPlant.${index}.id` as const)} />
            )}
            <Grid item xs={12} md={4.5}>
              <FormField
                label="Material Name"
                placeholder="Enter material name"
                disabled={disabled}
                error={!!errors.materialsNoPlant?.[index]?.name}
                helperText={errors.materialsNoPlant?.[index]?.name?.message}
                {...register(`materialsNoPlant.${index}.name` as const)}
              />
            </Grid>
            <Grid item xs={12} md={3.5}>
              <FormField
                label="Percentage (%)"
                placeholder="Enter percentage"
                type="number"
                disabled={disabled}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                error={!!errors.materialsNoPlant?.[index]?.percentage}
                helperText={errors.materialsNoPlant?.[index]?.percentage?.message}
                {...register(`materialsNoPlant.${index}.percentage` as const)}
              />
            </Grid>
            <Grid item xs={12} md={3.5}>
              <CurrencyFormField
                control={control}
                name={`materialsNoPlant.${index}.rate`}
                label="Rate"
                placeholder="Enter rate"
                disabled={disabled}
                error={!!errors.materialsNoPlant?.[index]?.rate}
                helperText={errors.materialsNoPlant?.[index]?.rate?.message}
              />
            </Grid>
            <Grid item xs={12} md={0.5}>
              {!disabled && (
                <MDBox display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                  <IconButton
                    //@ts-ignore
                    color="negative"
                    onClick={() => removeMaterial(index)}
                  >
                    <Icon fontSize="large" color="inherit">
                      delete
                    </Icon>
                  </IconButton>
                </MDBox>
              )}
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </MDBox>
  );
}
